import { useState } from 'react';
import { DndContext, DragOverlay, closestCorners, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import Header from '../../components/navigation/Header';
import FloatingActionButton from '../../components/navigation/FloatingActionButton';
import TaskModal from '../../components/navigation/TaskModal';
import QuadrantCard from './components/QuadrantCard';
import TaskCard from './components/TaskCard';
import SearchFilterBar from './components/SearchFilterBar';
import BulkActionToolbar from './components/BulkActionToolbar';
import TaskFormContent from './components/TaskFormContent';
import ToastNotification from './components/ToastNotification';

const MainDashboard = () => {
  const [tasks, setTasks] = useState([
    {
      id: 'task-1',
      title: 'Complete quarterly report presentation',
      notes: 'Prepare slides and data analysis for Q1 review meeting with stakeholders',
      dueDate: '2026-02-07',
      dueTime: '14:00',
      estimatedTime: '3h',
      tags: ['Work', 'Urgent'],
      quadrantId: 'urgent-important',
      completed: false,
      isUrgent: true,
      isImportant: true,
      recurrence: 'none',
      createdAt: new Date('2026-02-05T10:00:00')
    },
    {
      id: 'task-2',
      title: 'Review and update project roadmap',
      notes: 'Align team goals with company objectives for next quarter',
      dueDate: '2026-02-10',
      dueTime: '10:00',
      estimatedTime: '2h',
      tags: ['Planning', 'Strategy'],
      quadrantId: 'not-urgent-important',
      completed: false,
      isUrgent: false,
      isImportant: true,
      recurrence: 'monthly',
      createdAt: new Date('2026-02-04T09:00:00')
    },
    {
      id: 'task-3',
      title: 'Respond to client emails',
      notes: 'Address pending inquiries from three different clients',
      dueDate: '2026-02-07',
      dueTime: '16:00',
      estimatedTime: '1h',
      tags: ['Communication'],
      quadrantId: 'urgent-not-important',
      completed: false,
      isUrgent: true,
      isImportant: false,
      recurrence: 'daily',
      createdAt: new Date('2026-02-06T14:00:00')
    },
    {
      id: 'task-4',
      title: 'Organize digital files and folders',
      notes: 'Clean up desktop and archive old project files',
      dueDate: '2026-02-15',
      dueTime: '',
      estimatedTime: '1h 30m',
      tags: ['Organization'],
      quadrantId: 'not-urgent-not-important',
      completed: false,
      isUrgent: false,
      isImportant: false,
      recurrence: 'weekly',
      createdAt: new Date('2026-02-03T11:00:00')
    },
    {
      id: 'task-5',
      title: 'Schedule team building activity',
      notes: 'Plan and coordinate next month team outing',
      dueDate: '2026-02-12',
      dueTime: '',
      estimatedTime: '45m',
      tags: ['Team', 'Planning'],
      quadrantId: 'not-urgent-important',
      completed: false,
      isUrgent: false,
      isImportant: true,
      recurrence: 'none',
      createdAt: new Date('2026-02-02T15:00:00')
    },
    {
      id: 'task-6',
      title: 'Fix critical bug in production',
      notes: 'Users reporting login issues on mobile app',
      dueDate: '2026-02-07',
      dueTime: '12:00',
      estimatedTime: '2h',
      tags: ['Bug', 'Critical'],
      quadrantId: 'urgent-important',
      completed: true,
      isUrgent: true,
      isImportant: true,
      recurrence: 'none',
      createdAt: new Date('2026-02-07T08:00:00')
    }
  ]);

  const [quadrants] = useState([
    {
      id: 'urgent-important',
      title: 'Urgent & Important',
      subtitle: 'Do First',
      icon: 'AlertCircle',
      color: '#B85C5C'
    },
    {
      id: 'not-urgent-important',
      title: 'Not Urgent & Important',
      subtitle: 'Schedule',
      icon: 'Calendar',
      color: '#7BA05B'
    },
    {
      id: 'urgent-not-important',
      title: 'Urgent & Not Important',
      subtitle: 'Delegate',
      icon: 'Users',
      color: '#C4935A'
    },
    {
      id: 'not-urgent-not-important',
      title: 'Not Urgent & Not Important',
      subtitle: 'Eliminate',
      icon: 'Trash2',
      color: '#5A6B5A'
    }
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('dueDate');
  const [filterBy, setFilterBy] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [toast, setToast] = useState(null);
  const [taskFormData, setTaskFormData] = useState(null);
  const [undoStack, setUndoStack] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const getFilteredAndSortedTasks = (quadrantId) => {
    let filtered = tasks?.filter(task => task?.quadrantId === quadrantId);

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(task =>
        task?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        task?.notes?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        task?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
      );
    }

    // Apply status filter
    switch (filterBy) {
      case 'active':
        filtered = filtered?.filter(task => !task?.completed);
        break;
      case 'completed':
        filtered = filtered?.filter(task => task?.completed);
        break;
      case 'overdue':
        filtered = filtered?.filter(task => 
          new Date(task.dueDate) < new Date() && !task?.completed
        );
        break;
      case 'today':
        filtered = filtered?.filter(task => 
          new Date(task.dueDate)?.toDateString() === new Date()?.toDateString()
        );
        break;
      case 'week':
        const weekFromNow = new Date();
        weekFromNow?.setDate(weekFromNow?.getDate() + 7);
        filtered = filtered?.filter(task => 
          new Date(task.dueDate) <= weekFromNow && new Date(task.dueDate) >= new Date()
        );
        break;
    }

    // Apply sorting
    switch (sortBy) {
      case 'dueDate':
        filtered?.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        break;
      case 'createdDate':
        filtered?.sort((a, b) => b?.createdAt - a?.createdAt);
        break;
      case 'priority':
        filtered?.sort((a, b) => {
          if (a?.isUrgent && !b?.isUrgent) return -1;
          if (!a?.isUrgent && b?.isUrgent) return 1;
          if (a?.isImportant && !b?.isImportant) return -1;
          if (!a?.isImportant && b?.isImportant) return 1;
          return 0;
        });
        break;
    }

    return filtered;
  };

  const handleDragStart = (event) => {
    setActiveId(event?.active?.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeTask = tasks?.find(t => t?.id === active?.id);
    const overQuadrant = quadrants?.find(q => q?.id === over?.id);

    if (overQuadrant && activeTask?.quadrantId !== overQuadrant?.id) {
      setTasks(tasks?.map(task =>
        task?.id === active?.id
          ? { ...task, quadrantId: overQuadrant?.id }
          : task
      ));
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeTask = tasks?.find(t => t?.id === active?.id);
    const overTask = tasks?.find(t => t?.id === over?.id);

    if (overTask && activeTask?.quadrantId === overTask?.quadrantId) {
      const quadrantTasks = tasks?.filter(t => t?.quadrantId === activeTask?.quadrantId);
      const oldIndex = quadrantTasks?.findIndex(t => t?.id === active?.id);
      const newIndex = quadrantTasks?.findIndex(t => t?.id === over?.id);

      const reordered = arrayMove(quadrantTasks, oldIndex, newIndex);
      const otherTasks = tasks?.filter(t => t?.quadrantId !== activeTask?.quadrantId);
      setTasks([...otherTasks, ...reordered]);
    }

    showToast('Task moved successfully', 'success', () => {
      // Undo logic would go here
    });
  };

  const handleCreateTask = (quadrantId = null) => {
    setEditingTask(null);
    setTaskFormData({
      title: '',
      notes: '',
      dueDate: '',
      dueTime: '',
      estimatedTime: '',
      tags: [],
      isUrgent: quadrantId?.includes('urgent') || false,
      isImportant: quadrantId?.includes('important') || false,
      recurrence: 'none',
    });
    setIsModalOpen(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskFormData(task);
    setIsModalOpen(true);
  };

  const handleSaveTask = () => {
    if (!taskFormData || !taskFormData?.title || !taskFormData?.dueDate) {
      showToast('Please fill in required fields', 'error');
      return;
    }

    const quadrantId = 
      taskFormData?.isUrgent && taskFormData?.isImportant ? 'urgent-important' : !taskFormData?.isUrgent && taskFormData?.isImportant ?'not-urgent-important': taskFormData?.isUrgent && !taskFormData?.isImportant ?'urgent-not-important': 'not-urgent-not-important';

    if (editingTask) {
      setTasks(tasks?.map(task =>
        task?.id === editingTask?.id
          ? { ...taskFormData, id: task?.id, quadrantId, createdAt: task?.createdAt }
          : task
      ));
      showToast('Task updated successfully', 'success');
    } else {
      const newTask = {
        ...taskFormData,
        id: `task-${Date.now()}`,
        quadrantId,
        completed: false,
        createdAt: new Date()
      };
      setTasks([...tasks, newTask]);
      showToast('Task created successfully', 'success');
    }

    setIsModalOpen(false);
    setEditingTask(null);
    setTaskFormData(null);
  };

  const handleDeleteTask = (taskId) => {
    const taskToDelete = tasks?.find(t => t?.id === taskId);
    setUndoStack([...undoStack, { action: 'delete', task: taskToDelete }]);
    setTasks(tasks?.filter(task => task?.id !== taskId));
    showToast('Task deleted', 'success', () => handleUndo());
  };

  const handleToggleComplete = (taskId) => {
    setTasks(tasks?.map(task =>
      task?.id === taskId
        ? { ...task, completed: !task?.completed }
        : task
    ));
    const task = tasks?.find(t => t?.id === taskId);
    showToast(
      task?.completed ? 'Task marked as incomplete' : 'Task completed',
      'success'
    );
  };

  const handleSwipeAction = (taskId, action) => {
    if (action === 'complete') {
      handleToggleComplete(taskId);
    } else if (action === 'delete') {
      handleDeleteTask(taskId);
    }
  };

  const handleBulkComplete = () => {
    setTasks(tasks?.map(task => 
      selectedTasks?.includes(task?.id) ? { ...task, completed: true } : task
    ));
    setSelectedTasks([]);
    setIsBulkMode(false);
    showToast(`${selectedTasks?.length} tasks marked as complete`, 'success');
  };

  const handleBulkDelete = () => {
    const tasksToDelete = tasks?.filter(task => selectedTasks?.includes(task?.id));
    setUndoStack([...undoStack, { action: 'bulkDelete', tasks: tasksToDelete }]);
    
    setTasks(tasks?.filter(task => !selectedTasks?.includes(task?.id)));
    setSelectedTasks([]);
    setIsBulkMode(false);
    showToast(`${selectedTasks?.length} tasks deleted`, 'success', () => handleUndo());
  };

  const handleBulkMove = () => {
    showToast('Bulk move feature coming soon', 'warning');
  };

  const handleUndo = () => {
    if (undoStack?.length === 0) return;
    
    const lastAction = undoStack?.[undoStack?.length - 1];
    
    if (lastAction?.action === 'delete') {
      setTasks([...tasks, lastAction?.task]);
      setUndoStack(undoStack?.slice(0, -1));
      showToast('Task restored', 'success');
    } else if (lastAction?.action === 'bulkDelete') {
      setTasks([...tasks, ...lastAction?.tasks]);
      setUndoStack(undoStack?.slice(0, -1));
      showToast('Tasks restored', 'success');
    }
  };

  const showToast = (message, type = 'success', onUndo = null) => {
    setToast({ message, type, onUndo });
  };

  const activeTask = activeId ? tasks?.find(t => t?.id === activeId) : null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-24 md:pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-2">
              Eisenhower Matrix
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Organize and prioritize your tasks effectively
            </p>
          </div>

          {/* Search & Filter Bar */}
          <SearchFilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sortBy={sortBy}
            onSortChange={setSortBy}
            filterBy={filterBy}
            onFilterChange={setFilterBy}
            onBulkMode={() => setIsBulkMode(!isBulkMode)}
          />

          {/* Quadrant Grid */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 lg:gap-6">
              {quadrants?.map(quadrant => (
                <QuadrantCard
                  key={quadrant?.id}
                  quadrant={quadrant}
                  tasks={getFilteredAndSortedTasks(quadrant?.id)}
                  onAddTask={handleCreateTask}
                  onEditTask={handleEditTask}
                  onDeleteTask={handleDeleteTask}
                  onToggleComplete={handleToggleComplete}
                  onSwipeAction={handleSwipeAction}
                />
              ))}
            </div>

            <DragOverlay>
              {activeTask ? (
                <div className="opacity-80">
                  <TaskCard
                    task={activeTask}
                    quadrantColor="#6B8E6B"
                    onEdit={() => {}}
                    onDelete={() => {}}
                    onToggleComplete={() => {}}
                    onSwipeAction={() => {}}
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </main>
      {/* Floating Action Button */}
      <FloatingActionButton
        onCreateTask={() => handleCreateTask()}
        isModalOpen={isModalOpen}
      />
      {/* Task Modal */}
      <TaskModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTask(null);
          setTaskFormData(null);
        }}
        onSave={handleSaveTask}
        title={editingTask ? 'Edit Task' : 'Create Task'}
      >
        <TaskFormContent
          task={taskFormData}
          onChange={setTaskFormData}
        />
      </TaskModal>
      {/* Bulk Action Toolbar */}
      {isBulkMode && selectedTasks?.length > 0 && (
        <BulkActionToolbar
          selectedCount={selectedTasks?.length}
          onComplete={handleBulkComplete}
          onDelete={handleBulkDelete}
          onMove={handleBulkMove}
          onCancel={() => {
            setIsBulkMode(false);
            setSelectedTasks([]);
          }}
        />
      )}
      {/* Toast Notification */}
      {toast && (
        <ToastNotification
          message={toast?.message}
          type={toast?.type}
          onUndo={toast?.onUndo}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default MainDashboard;